import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMunicipality } from '../hooks/useMunicipalities';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Reveal from '../components/sections/Reveal';
import { ErrorState } from '../components/ui/ErrorState';
import { LoadingScreen } from '../components/ui/LoadingScreen';

const MunicipalityPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { data: city, isLoading, error } = useMunicipality(slug || '');

    if (isLoading) return <LoadingScreen message="Carregando município..." />;

    if (error || !city) {
        return (
            <ErrorState title="Município não encontrado" description="Este município não existe ou está indisponível no momento.">
                <button
                    onClick={() => navigate('/')}
                    className="bg-brand-green text-white px-6 py-3 rounded-xl font-bold hover:bg-brand-darkGreen transition-colors"
                >
                    Voltar para a Home
                </button>
            </ErrorState>
        );
    }

    // Default contact if missing
    const contact = {
        address: city.contact_address || 'Rua Principal, 100 - Centro',
        phone: city.contact_phone || '(51) 99999-9999',
        email: city.contact_email || `conselho.${city.slug}@aconturs.com.br`,
        hours: city.contact_hours || 'Segunda a Sexta: 08:00 - 12:00 / 13:30 - 17:30'
    };

    // Use team from DB or mock if empty for visual only
    const team = city.team || [
        { name: 'Conselheiro 1', image: 'https://ui-avatars.com/api/?name=C+1&background=random' },
        { name: 'Conselheira 2', image: 'https://ui-avatars.com/api/?name=C+2&background=random' },
        { name: 'Conselheiro 3', image: 'https://ui-avatars.com/api/?name=C+3&background=random' },
    ];

    return (
        <div className="font-sans text-slate-800 antialiased bg-slate-50 min-h-screen flex flex-col">
            <Navbar />

            <main className="flex-grow pt-24 md:pt-32 pb-16">
                <div className="container mx-auto px-4">

                    {/* 1. Header: City Name + Standard Text */}
                    <Reveal>
                        <header className="mb-12 text-center">
                            <h1 className="text-4xl md:text-5xl font-extrabold text-brand-darkGreen mb-4">{city.name}</h1>
                            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                                O Conselho Tutelar de {city.name} atua permanentemente na defesa dos direitos da criança e do adolescente,
                                sendo a porta de entrada para a rede de proteção no município.
                            </p>
                            <div className="w-24 h-1 bg-brand-yellow mx-auto mt-6 rounded-full"></div>
                        </header>
                    </Reveal>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                        {/* Left Column: Contact & Identity */}
                        <div className="lg:col-span-4 space-y-8">

                            {/* 3. Visual Identity: Logo */}
                            <Reveal delay={100}>
                                <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-brand-green text-center">
                                    <div className="w-48 h-48 mx-auto mb-6 relative">
                                        <div className="absolute inset-0 bg-brand-yellow/10 rounded-full blur-xl transform scale-90"></div>
                                        <img
                                            src={city.image || 'https://via.placeholder.com/150'}
                                            alt={`Brasão ${city.name}`}
                                            className="w-full h-full object-contain relative z-10 drop-shadow-md hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <span className="inline-block bg-brand-green/10 text-brand-darkGreen px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                                        Membro Regional
                                    </span>
                                </div>
                            </Reveal>

                            {/* 2. Contact Data */}
                            <Reveal delay={200}>
                                <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-brand-yellow">
                                    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                                        <i className="fas fa-address-card text-brand-yellow"></i>
                                        Canais de Atendimento
                                    </h3>
                                    <ul className="space-y-4">
                                        <li className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                                                <i className="fas fa-map-marker-alt"></i>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-slate-400 uppercase block">Endereço</span>
                                                <p className="text-slate-700 font-medium">{contact.address}</p>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition">
                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
                                                <i className="fas fa-phone-alt"></i>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-slate-400 uppercase block">Telefone / Plantão</span>
                                                <p className="text-slate-700 font-medium">{contact.phone}</p>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                                                <i className="fas fa-envelope"></i>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-slate-400 uppercase block">E-mail</span>
                                                <p className="text-slate-700 font-medium break-all">{contact.email}</p>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition">
                                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 shrink-0">
                                                <i className="fas fa-clock"></i>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-slate-400 uppercase block">Horário</span>
                                                <p className="text-slate-700 font-medium">{contact.hours}</p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </Reveal>
                        </div>

                        {/* Right Column: Map, Team, History */}
                        <div className="lg:col-span-8 space-y-12">

                            {/* 3. Global Map Identity */}
                            <Reveal delay={300}>
                                <div className="bg-white p-2 rounded-2xl shadow-lg">
                                    <div className="bg-slate-100 rounded-xl overflow-hidden relative h-[300px] flex items-center justify-center">
                                        <span className="absolute top-4 left-4 bg-white/80 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-slate-500 shadow-sm">
                                            <i className="fas fa-map mr-2"></i>Localização na Região
                                        </span>
                                        {/* Placeholder for the specific map highlighting the city */}
                                        <div className="text-center text-slate-400">
                                            <i className="fas fa-map-marked-alt text-6xl mb-4 opacity-30"></i>
                                            <p>Mapa Individual de {city.name}</p>
                                            <p className="text-xs">(Em breve)</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                            {/* 4. Current Team */}
                            <Reveal delay={400}>
                                <div>
                                    <h3 className="text-2xl font-bold text-brand-darkGreen mb-8 border-l-4 border-brand-green pl-4">
                                        Colegiado Atual <span className="text-sm font-normal text-slate-500 ml-2">(Gestão 2024-2028)</span>
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
                                        {team.map((member: any, idx) => (
                                            <div key={idx} className="group text-center">
                                                <div className="w-24 h-24 mx-auto mb-3 rounded-full border-2 border-brand-yellow p-1 overflow-hidden shadow-md group-hover:scale-110 transition-transform duration-300">
                                                    <img
                                                        src={member.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || 'CT')}&background=006837&color=fff`}
                                                        alt={member.name}
                                                        className="w-full h-full rounded-full object-cover"
                                                    />
                                                </div>
                                                <h4 className="font-bold text-slate-800 text-sm group-hover:text-brand-green transition-colors">{member.name}</h4>
                                                <span className="text-[10px] uppercase font-bold text-slate-400">{member.role || 'Conselheiro'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>

                            {/* 5. History */}
                            <Reveal delay={500}>
                                <div className="bg-slate-100 rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-xl font-bold text-slate-700 mb-6 flex items-center gap-2">
                                        <i className="fas fa-history text-slate-400"></i>
                                        Memória do Conselho
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-slate-300">
                                            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                                                {city.history || "História e registros em fase de levantamento pela comissão regional."}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                        </div>

                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default MunicipalityPage;
